import {
	type DataSources,
	type DraftDataSources,
	type DraftEmissionRequestFormData,
	type DraftEmissionValues,
	type DraftMethodologies,
	type DraftReductionPlan,
	type DraftVerificationData,
	type EmissionRequestProductFormData,
	type EmissionsDataRequestResponse,
	type EmissionValues,
	type Methodologies,
	type ProductEmissionDataRequestDetailsResponse,
	ProductResponseProductRequestStatusEnum,
	type ReductionInitiative,
	type ReductionPlan,
	type TemplateDetailsResponse,
	type VerificationData,
} from '@api-client'
import {
	createSelector,
	createSlice,
	type PayloadAction,
} from '@reduxjs/toolkit'
import type { RootState } from '@store/store'

// This is a state of our slice
interface IState {
	// Our actual data from all the forms, in the format we should send to saveDraft or saveFinalData
	data: EmissionRequestProductFormData | DraftEmissionRequestFormData
	// Here we have product or enditem metadata for example product name, desc, ref#
	itemData:
		| ProductEmissionDataRequestDetailsResponse
		| TemplateDetailsResponse
		| undefined
	// Details of request (filled only for request and null for endItems of I'm not wrong), contains supplier details etc.
	requestData: EmissionsDataRequestResponse | undefined
	// Just error state for initial load
	error: boolean
}

// This interface is needed for initStore action, actually it mimics IState
interface CoreStoreState {
	data: EmissionRequestProductFormData | DraftEmissionRequestFormData
	item?: ProductEmissionDataRequestDetailsResponse | TemplateDetailsResponse
	requestData?: EmissionsDataRequestResponse
	error: boolean
}

const initialState: IState = {
	data: {},
	itemData: undefined,
	requestData: undefined,
	error: false,
}

const emissionDataFormSlice = createSlice({
	name: 'emissionDataFormSlice',
	initialState,
	reducers: {
		pushCommentToStore: (
			state: IState,
			action: PayloadAction<string | undefined>
		) => {
			state.data.comment = action.payload
		},
		pushEmissionsToStore: (
			state: IState,
			action: PayloadAction<EmissionValues | DraftEmissionValues>
		) => {
			state.data.emissionValues = action.payload
		},
		pushDataToStore: (
			state: IState,
			action: PayloadAction<DataSources | DraftDataSources>
		) => {
			state.data.dataSources = action.payload
		},
		pushMethodologiesToStore: (
			state: IState,
			action: PayloadAction<Methodologies | DraftMethodologies | undefined>
		) => {
			state.data.methodologies = action.payload
		},
		pushVerificationDataToStore: (
			state: IState,
			action: PayloadAction<
				VerificationData | DraftVerificationData | undefined
			>
		) => {
			state.data.verificationData = action.payload
		},
		pushReductionPlanToStore: (
			state: IState,
			action: PayloadAction<ReductionPlan | DraftReductionPlan | undefined>
		) => {
			state.data.reductionPlan = action.payload
		},
		pushReductionInitiativesToStore: (
			state: IState,
			action: PayloadAction<readonly ReductionInitiative[] | undefined>
		) => {
			state.data.reductionInitiatives = action.payload
		},

		initStore: (state: IState, action: PayloadAction<CoreStoreState>) => {
			state.data = action?.payload.data
			state.itemData = action?.payload.item
			state.requestData = action?.payload.requestData
			state.error = action?.payload.error
		},
		pushTemplateToStore: (
			state: IState,
			action: PayloadAction<
				EmissionRequestProductFormData | DraftEmissionRequestFormData
			>
		) => {
			state.data = action?.payload as
				| EmissionRequestProductFormData
				| DraftEmissionRequestFormData
		},
	},
})

export const getProductInfo = createSelector(
	(state: RootState) => state.emissionDataForm.itemData,
	(_: unknown, isTemplate: boolean) => isTemplate,
	(itemData, isTemplate) => {
		if (itemData) {
			const productName = isTemplate
				? (itemData as TemplateDetailsResponse).name
				: (itemData as ProductEmissionDataRequestDetailsResponse)
						?.productInformation?.name
			const productReference = isTemplate
				? (itemData as TemplateDetailsResponse).internalReference
				: (itemData as ProductEmissionDataRequestDetailsResponse)
						?.productInformation?.internalReference
			const productDesc = isTemplate
				? (itemData as TemplateDetailsResponse).description
				: (itemData as ProductEmissionDataRequestDetailsResponse)
						?.productInformation?.description
			const productRequestComment = isTemplate
				? undefined
				: (itemData as ProductEmissionDataRequestDetailsResponse)
						?.productInformation?.requestComment
			const productReferenceYear = isTemplate
				? undefined
				: (itemData as ProductEmissionDataRequestDetailsResponse)?.emissionData
						?.emissionValues.referenceYear
			const rejectionComment =
				(itemData as ProductEmissionDataRequestDetailsResponse)
					?.productInformation?.reason || undefined
			const unit = isTemplate
				? (itemData as TemplateDetailsResponse)?.unit
				: (itemData as ProductEmissionDataRequestDetailsResponse)
						?.productInformation?.unit || undefined
			const featureFlags = isTemplate
				? (itemData as TemplateDetailsResponse)?.featureFlags
				: (itemData as ProductEmissionDataRequestDetailsResponse)?.featureFlags
			const productOrServiceCategory = itemData?.productOrServiceCategory
			return {
				featureFlags,
				productName,
				productReference,
				productDesc,
				productReferenceYear,
				productRequestComment,
				productOrServiceCategory,
				rejectionComment,
				unit,
				purchasingPeriodStart: isTemplate
					? undefined
					: (itemData as ProductEmissionDataRequestDetailsResponse)
							?.productInformation?.purchasingPeriodStart,
				purchasingPeriodEnd: isTemplate
					? undefined
					: (itemData as ProductEmissionDataRequestDetailsResponse)
							?.productInformation?.purchasingPeriodEnd,
			}
		}

		return { productName: '', productReference: '', productDesc: '' }
	}
)

export const getEditingPermission = createSelector(
	(state: RootState) => state.emissionDataForm.itemData,
	(_: unknown, isTemplate: boolean) => isTemplate,
	(itemData, isTemplate) =>
		!isTemplate &&
		(itemData as unknown as ProductEmissionDataRequestDetailsResponse)
			?.productInformation?.productRequestStatus ===
			ProductResponseProductRequestStatusEnum.REJECTED
)

export const getCompanyName = createSelector(
	(state: RootState) => state.emissionDataForm.requestData,
	requestData =>
		(requestData as unknown as EmissionsDataRequestResponse)?.customer?.name ||
		undefined
)

export const {
	pushCommentToStore,
	pushEmissionsToStore,
	pushDataToStore,
	pushMethodologiesToStore,
	pushReductionInitiativesToStore,
	pushReductionPlanToStore,
	pushVerificationDataToStore,
	initStore,
	pushTemplateToStore,
} = emissionDataFormSlice.actions

export default emissionDataFormSlice.reducer
